import { useEffect, useRef, useState } from "react";

const PERIOD_MS = 30000;

// Emails just moved to Done — Graph is eventually consistent, so keep them
// out of the list for 90s even if a refetch still returns them.
export function useHiddenEmails(setData) {
  const hiddenRef = useRef(new Map());
  const stripHidden = (r) => {
    const now = Date.now();
    for (const [id, until] of hiddenRef.current) if (until < now) hiddenRef.current.delete(id);
    if (!r?.emails || hiddenRef.current.size === 0) return r;
    const emails = r.emails.filter(e => !hiddenRef.current.has(e.id));
    const dropped = r.emails.length - emails.length;
    return { ...r, emails, total: Math.max(0, (r.total ?? r.emails.length) - dropped) };
  };
  const hideEmail = (id) => {
    if (!id) return;
    hiddenRef.current.set(id, Date.now() + 90000);
    setData(prev => stripHidden(prev));
  };
  return { stripHidden, hideEmail };
}

// Fixed 30s cadence anchored to the wall clock — the cycle keeps ticking
// regardless of page/mounts and a manual pull never restarts it. `onTick`
// also fires when the tab regains focus.
export function usePullCadence(onTick) {
  const [countdown, setCountdown] = useState(30 - Math.floor((Date.now() / 1000) % 30));
  const tickRef = useRef(onTick);
  tickRef.current = onTick;
  useEffect(() => {
    let lastPeriod = Math.floor(Date.now() / PERIOD_MS);
    const t = setInterval(() => {
      const now = Date.now();
      setCountdown(30 - Math.floor((now / 1000) % 30));
      const period = Math.floor(now / PERIOD_MS);
      if (period !== lastPeriod) {
        lastPeriod = period;
        if (document.visibilityState === "visible") tickRef.current();
      }
    }, 1000);
    const onVisible = () => { if (document.visibilityState === "visible") tickRef.current(); };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);
  return countdown;
}
